import { useEffect, useState } from "react";
import { Input, Select, Typography } from "antd";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import type { AiInsightsComponentSpec, AiResponseLength } from "../../Processing/types";

const { Text } = Typography;
const { TextArea } = Input;

const MAX_QUERY_LENGTH = 512;

const EXAMPLE_QUERIES = [
  "What stands out on this dashboard?",
  "Who trained the most yesterday?",
  "Compare this week's total distance to last week's",
];

const RESPONSE_LENGTH_OPTIONS: { value: AiResponseLength; label: string }[] = [
  { value: "brief", label: "Brief" },
  { value: "standard", label: "Standard" },
  { value: "extended", label: "Extended" },
];

/**
 * ponytail: two fields of state, so plain useState instead of
 * useConfiguratorSync's reducer. A widget saved before `response_length`
 * existed may carry `detail_level` and `instructions`. They are ignored on
 * purpose - it opens as unset (Standard, empty question) and the next save
 * writes only the current fields.
 */
export function AiInsightsConfigurator({
  onSpecChange,
  existingSpec,
}: ConfiguratorSharedProps<AiInsightsComponentSpec>) {
  const [query, setQuery] = useState(existingSpec?.query ?? "");
  const [responseLength, setResponseLength] = useState<AiResponseLength>(
    existingSpec?.response_length ?? "standard",
  );

  useEffect(() => {
    onSpecChange({ type: "ai_insights", query, response_length: responseLength });
  }, [query, responseLength, onSpecChange]);

  return (
    <div className="awm-simple-form">
      <Text strong style={{ fontSize: 13 }}>Question</Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Ask about this dashboard the way you'd ask in chat. The answer renders in the tile.
      </Text>
      <TextArea
        placeholder={EXAMPLE_QUERIES[0]}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        maxLength={MAX_QUERY_LENGTH}
        showCount
        autoSize={{ minRows: 3, maxRows: 6 }}
      />
      <Text type="secondary" style={{ fontSize: 11, marginTop: 8 }}>
        For example:
        {EXAMPLE_QUERIES.map((q) => (
          <span key={q} style={{ display: "block", fontStyle: "italic" }}>“{q}”</span>
        ))}
      </Text>
      <Text strong style={{ fontSize: 13, marginTop: 16 }}>Response length</Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        How long the answer is. A question that asks for more or less detail overrides this, except for /insights.
      </Text>
      <Select
        value={responseLength}
        onChange={setResponseLength}
        options={RESPONSE_LENGTH_OPTIONS}
      />
    </div>
  );
}
